import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import BASE_QUERY from '@/features/baseQuery';
import { firestore } from '@/utils/firebase';
import { normalizeSignatureEmail } from '@/features/unlayerUserSignatures/helpers';

const UNLAYER_USER_SIGNATURES_COLLECTION = 'unlayerUserSignatures';

const toError = (error, fallbackMessage) => ({
	status: error?.code ?? 'CUSTOM_ERROR',
	data: {
		message:
			typeof error?.message === 'string' && error.message.length > 0
				? error.message
				: fallbackMessage,
	},
});

const toRequiredString = (value, fieldName) => {
	const normalizedValue = String(value ?? '').trim();
	if (!normalizedValue) {
		throw new Error(`${fieldName} is required`);
	}
	return normalizedValue;
};

const getUnlayerUserSignatureDocId = ({ userId, email }) => {
	const normalizedUserId = toRequiredString(userId, 'userId');
	const normalizedEmail = normalizeSignatureEmail(email);
	if (!normalizedEmail) {
		throw new Error('email is required');
	}
	return `${normalizedUserId}__${normalizedEmail}`;
};

const parseStoredDesignJson = (storedDesignJson) => {
	if (typeof storedDesignJson !== 'string' || storedDesignJson.trim().length === 0) {
		return {
			designJson: null,
			designJsonInvalid: false,
			designJsonError: '',
		};
	}

	try {
		return {
			designJson: JSON.parse(storedDesignJson),
			designJsonInvalid: false,
			designJsonError: '',
		};
	} catch (error) {
		return {
			designJson: null,
			designJsonInvalid: true,
			designJsonError: 'Saved signature design could not be loaded.',
		};
	}
};

const serializeSignatureRecord = (snapshot) => {
	const data = snapshot?.data?.() ?? {};
	const parsedDesign = parseStoredDesignJson(data?.designJson);
	return {
		id: snapshot.id,
		userId: data?.userId ? String(data.userId) : '',
		email: normalizeSignatureEmail(data?.email),
		html: typeof data?.html === 'string' ? data.html : '',
		designJson: parsedDesign.designJson,
		designJsonInvalid: parsedDesign.designJsonInvalid,
		designJsonError: parsedDesign.designJsonError,
		createdAt: data?.createdAt?.toMillis?.() ?? 0,
		updatedAt: data?.updatedAt?.toMillis?.() ?? 0,
	};
};

export const unlayerUserSignaturesApi = createApi({
	reducerPath: 'unlayerUserSignaturesApi',
	tagTypes: ['UNLAYER_USER_SIGNATURES', 'UNLAYER_USER_SIGNATURE'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getUnlayerUserSignature: builder.query({
			providesTags: (result, error, { userId, email }) => [
				{
					type: 'UNLAYER_USER_SIGNATURE',
					id: getUnlayerUserSignatureDocId({ userId, email }),
				},
			],
			queryFn: async ({ userId, email }) => {
				try {
					const signatureRef = doc(
						firestore,
						UNLAYER_USER_SIGNATURES_COLLECTION,
						getUnlayerUserSignatureDocId({ userId, email }),
					);
					const snapshot = await getDoc(signatureRef);
					if (!snapshot.exists()) {
						return { data: null };
					}
					return { data: serializeSignatureRecord(snapshot) };
				} catch (error) {
					console.error('Error fetching Unlayer user signature', error);
					return { error: toError(error, 'Unable to load signature') };
				}
			},
		}),
		getUnlayerUserSignatures: builder.query({
			providesTags: (result) => [
				'UNLAYER_USER_SIGNATURES',
				...(result ?? []).map((record) => ({
					type: 'UNLAYER_USER_SIGNATURE',
					id: record.id,
				})),
			],
			queryFn: async ({ userId, availableEmails = [] }) => {
				try {
					const normalizedUserId = toRequiredString(userId, 'userId');
					const normalizedEmails = Array.from(
						new Set(
							(availableEmails ?? [])
								.map((email) => normalizeSignatureEmail(email))
								.filter(Boolean),
						),
					);

					if (normalizedEmails.length === 0) {
						return { data: [] };
					}

					const emailOrderMap = new Map(
						normalizedEmails.map((normalizedEmail, index) => [normalizedEmail, index]),
					);
					// One read per signature (doc id `{userId}__{email}`): the Firestore rule
					// checks the doc id, so a principal user can read a colleague's signatures.
					const signatureSnapshots = await Promise.all(
						normalizedEmails.map((email) =>
							getDoc(
								doc(
									firestore,
									UNLAYER_USER_SIGNATURES_COLLECTION,
									getUnlayerUserSignatureDocId({
										userId: normalizedUserId,
										email,
									}),
								),
							),
						),
					);
					const signatures = signatureSnapshots
						.filter((snapshot) => snapshot.exists())
						.map((snapshot) => serializeSignatureRecord(snapshot))
						.sort(
							(left, right) =>
								(emailOrderMap.get(left.email) ?? Number.MAX_SAFE_INTEGER) -
								(emailOrderMap.get(right.email) ?? Number.MAX_SAFE_INTEGER),
						);

					return { data: signatures };
				} catch (error) {
					console.error('Error fetching Unlayer user signatures', error);
					return { error: toError(error, 'Unable to load signatures') };
				}
			},
		}),
		upsertUnlayerUserSignature: builder.mutation({
			invalidatesTags: (result, error, { userId, email }) => [
				'UNLAYER_USER_SIGNATURES',
				{
					type: 'UNLAYER_USER_SIGNATURE',
					id: getUnlayerUserSignatureDocId({ userId, email }),
				},
			],
			queryFn: async ({ userId, email, designJson, html }) => {
				try {
					const normalizedUserId = toRequiredString(userId, 'userId');
					const normalizedEmail = normalizeSignatureEmail(email);
					const serializedDesignJson = JSON.stringify(designJson ?? null);
					const normalizedHtml = String(html ?? '').trim();

					if (!normalizedEmail) {
						throw new Error('email is required');
					}
					if (!normalizedHtml) {
						throw new Error('html is required');
					}

					const now = Timestamp.now();
					const signatureId = getUnlayerUserSignatureDocId({
						userId: normalizedUserId,
						email: normalizedEmail,
					});
					const signatureRef = doc(
						firestore,
						UNLAYER_USER_SIGNATURES_COLLECTION,
						signatureId,
					);
					const existingSnapshot = await getDoc(signatureRef);
					const payload = {
						userId: normalizedUserId,
						email: normalizedEmail,
						designJson: serializedDesignJson,
						html: normalizedHtml,
						updatedAt: now,
						...(existingSnapshot.exists() ? {} : { createdAt: now }),
					};

					await setDoc(signatureRef, payload, { merge: true });
					const nextSnapshot = await getDoc(signatureRef);
					return { data: serializeSignatureRecord(nextSnapshot) };
				} catch (error) {
					console.error('Error saving Unlayer user signature', error);
					return { error: toError(error, 'Unable to save signature') };
				}
			},
		}),
	}),
});

export const {
	useGetUnlayerUserSignatureQuery,
	useGetUnlayerUserSignaturesQuery,
	useUpsertUnlayerUserSignatureMutation,
} = unlayerUserSignaturesApi;
